import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { handleApiError } from './axiosConfig';
import { Camp, Organizer } from '../utils/types';
import { BookingDetails, BookingParticipant } from './bookings';

// Response types
export interface OrganizersResponse {
  organizers: Organizer[];
}

export interface OrganizerCampsResponse {
  camps: Camp[];
}

// GET /api/organizers/{organizer_id} - Get single organizer
export const useGetOrganizer = (organizerId: string | undefined) => {
  return useQuery({
    queryKey: ['organizer', organizerId],
    queryFn: async (): Promise<Organizer> => {
      if (!organizerId) throw new Error('Organizer ID is required');
      try {
        return await apiClient.get<Organizer>(`/api/organizers/${organizerId}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch organizer:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: Boolean(organizerId),
  });
};

// PATCH /api/organizers/{organizer_id} - Update organizer details
export const useUpdateOrganizer = (organizerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; description: string | null }): Promise<Organizer> => {
      try {
        return await apiClient.patch<Organizer>(`/api/organizers/${organizerId}`, data);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to update organizer:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizer', organizerId] });
    },
  });
};

// POST /api/organizers/{organizer_id}/images/upload-url - Get presigned S3 upload URL for logo
export const useGetOrganizerLogoUploadUrl = (organizerId: string) => {
  return useMutation({
    mutationFn: async (): Promise<CampImageUploadUrlResponse> => {
      try {
        return await apiClient.post<CampImageUploadUrlResponse>(
          `/api/organizers/${organizerId}/images/upload-url`,
          {}
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to get organizer logo upload URL:', errorMessage);
        throw new Error(errorMessage);
      }
    },
  });
};

// POST /api/organizers/{organizer_id}/images - Save uploaded organizer logo
export const useSaveOrganizerLogo = (organizerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (key: string): Promise<Organizer> => {
      try {
        return await apiClient.post<Organizer>(
          `/api/organizers/${organizerId}/images`,
          { temp_s3_key: key, image_type: 'default' }
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to save organizer logo:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizer', organizerId] });
    },
  });
};

// GET /api/organizers - List all organizers
export const useGetOrganizers = () => {
  return useQuery({
    queryKey: ['organizers'],
    queryFn: async (): Promise<OrganizersResponse> => {
      try {
        return await apiClient.get<OrganizersResponse>('/api/organizers');
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch organizers:', errorMessage);
        throw new Error(errorMessage);
      }
    },
  });
};

export interface CampBookingsResponse {
  bookings: BookingDetails[];
  total: number;
  page: number;
  per_page: number;
}

// Booking in camp date response (flat contact fields)
export interface CampDateBooking {
  uuid: string;
  status: string;
  booking_type?: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_relationship: string | null;
  participants: AdminParticipant[];
}

// Camp date admin response
export interface CampDateAdmin {
  id: number;
  start_date: string;
  end_date: string;
  max_capacity: number | null;
  current_capacity: number;
  sessions: string[];
  bookings: CampDateBooking[];
}

// Response type for GET /organizers/{organizer_id}/camps/{camp_id}/dates/{date_id}/bookings
export interface CampDateBookingsResponse {
  camp: Camp;
  camp_date: CampDateAdmin;
  bookings: CampDateBooking[];
}

// Camp date with bookings (used by useGetCampDates)
export interface CampDateWithBookings {
  id: number;
  start_date: string;
  end_date: string;
  max_capacity: number | null;
  current_capacity: number;
  sessions: string[];
  price: number;
  bookings: CampDateBooking[];
}

// Response type for GET /organizers/{organizer_id}/camps/{camp_id}/dates
export interface CampDatesResponse {
  camp: Camp;
  dates: CampDateWithBookings[];
}

export interface AdminParticipant extends BookingParticipant {
  id: number;
  deleted_at?: string;
  status?: string;
}

export interface CreateAdminBookingRequest {
  participants: {
    name: string;
    birthdate?: string;
    details?: string;
  }[];
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_relationship_to_participant?: string;
}

// GET /organizers/{organizer_id}/camps - List camps for organizer
export const useGetOrganizerCamps = (organizerId: string | undefined) => {
  return useQuery({
    queryKey: ['organizer-admin-camps', organizerId],
    queryFn: async (): Promise<OrganizerCampsResponse> => {
      if (!organizerId) {
        throw new Error('Organizer ID is required');
      }
      try {
        return await apiClient.get<OrganizerCampsResponse>(`/api/organizers/${organizerId}/camps`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch organizer camps:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: Boolean(organizerId),
  });
};

// GET /organizers/{organizer_id}/camps/{camp_id}/dates - List camp dates with bookings
export const useGetCampDates = (
  organizerId: string | undefined,
  campId: string | undefined
) => {
  return useQuery({
    queryKey: ['camp-dates', organizerId, campId],
    queryFn: async (): Promise<CampDatesResponse> => {
      if (!organizerId || !campId) {
        throw new Error('Organizer ID and Camp ID are required');
      }
      try {
        const url = `/api/organizers/${organizerId}/camps/${campId}/dates`;
        return await apiClient.get<CampDatesResponse>(url);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch camp dates:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: Boolean(organizerId) && Boolean(campId),
  });
};

// GET /organizers/{organizer_id}/camps/{camp_id}/dates/{date_id}/bookings - Get camp date with bookings
export const useGetCampDateBookings = (
  organizerId: string | undefined,
  campId: string | undefined,
  dateId: string | undefined
) => {
  return useQuery({
    queryKey: ['camp-date-bookings', organizerId, campId, dateId],
    queryFn: async (): Promise<CampDateBookingsResponse> => {
      if (!organizerId || !campId || !dateId) {
        throw new Error('Organizer ID, Camp ID, and Date ID are required');
      }
      try {
        const url = `/api/organizers/${organizerId}/camps/${campId}/dates/${dateId}/bookings`;
        return await apiClient.get<CampDateBookingsResponse>(url);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch camp date bookings:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: Boolean(organizerId) && Boolean(campId) && Boolean(dateId),
  });
};

// GET /organizers/{organizer_id}/bookings/{booking_uuid} - Get booking details with participants
export const useGetBookingDetails = (
  organizerId: string | undefined,
  bookingUuid: string | undefined
) => {
  return useQuery({
    queryKey: ['admin-booking-details', organizerId, bookingUuid],
    queryFn: async (): Promise<BookingDetails> => {
      if (!organizerId || !bookingUuid) {
        throw new Error('Organizer ID and Booking UUID are required');
      }
      try {
        return await apiClient.get<BookingDetails>(
          `/api/organizers/${organizerId}/bookings/${bookingUuid}`
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch booking details:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    enabled: Boolean(organizerId) && Boolean(bookingUuid),
  });
};

// POST /organizers/{organizer_id}/camps/{camp_id}/dates/{camp_date_id}/bookings - Create admin booking
export const useCreateAdminBooking = (organizerId: string, campId: string, campDateId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAdminBookingRequest): Promise<CampDateBooking> => {
      try {
        return await apiClient.post<CampDateBooking>(
          `/api/organizers/${organizerId}/camps/${campId}/dates/${campDateId}/bookings`,
          data
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to create admin booking:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['camp-date-bookings', organizerId, campId] });
      queryClient.invalidateQueries({ queryKey: ['camp-dates', organizerId, campId] });
      queryClient.invalidateQueries({ queryKey: ['admin-booking-details', organizerId] });
    },
  });
};

// DELETE /organizers/{organizer_id}/bookings/{booking_id}/participants/{participant_id} - Soft delete participant
export const useDeleteParticipant = (organizerId: string, bookingId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (participantId: string): Promise<void> => {
      try {
        await apiClient.delete(
          `/api/organizers/${organizerId}/bookings/${bookingId}/participants/${participantId}`
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to delete participant:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['camp-date-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['camp-dates'] });
      queryClient.invalidateQueries({ queryKey: ['admin-booking-details', organizerId] });
    },
  });
};

// PATCH /organizers/{organizer_id}/bookings/{booking_id}/participants/{participant_id}/restore - Restore participant
export const useRestoreParticipant = (organizerId: string, bookingId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (participantId: string): Promise<AdminParticipant> => {
      try {
        return await apiClient.patch<AdminParticipant>(
          `/api/organizers/${organizerId}/bookings/${bookingId}/participants/${participantId}/restore`
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to restore participant:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['camp-date-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['camp-dates'] });
      queryClient.invalidateQueries({ queryKey: ['admin-booking-details', organizerId] });
    },
  });
};

// PATCH /organizers/{organizer_id}/camps/{camp_id}/dates/{camp_date_id}/capacity - Update camp date capacity
export const useUpdateCampDateCapacity = (organizerId: string, campId: string, campDateId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { max_capacity: number | null }): Promise<CampDateWithBookings> => {
      try {
        return await apiClient.patch<CampDateWithBookings>(
          `/api/organizers/${organizerId}/camps/${campId}/dates/${campDateId}/capacity`,
          data
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to update camp date capacity:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['camp-date-bookings', organizerId, campId] });
      queryClient.invalidateQueries({ queryKey: ['camp-dates', organizerId, campId] });
    },
  });
};

// PATCH /organizers/{organizer_id}/camps/{camp_id} - Update camp details
export const useUpdateCamp = (organizerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campId, data }: { campId: string; data: { name: string; description: string | null; additional_info: string | null; min_age: number | null; max_age: number | null; start_time: string | null; end_time: string | null; price: number | null } }): Promise<Camp> => {
      try {
        return await apiClient.patch<Camp>(
          `/api/organizers/${organizerId}/camps/${campId}`,
          data
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to update camp:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizer-admin-camps', organizerId] });
    },
  });
};

// POST /organizers/{organizer_id}/camps/{camp_id}/dates - Create camp date
export const useCreateCampDate = (organizerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campId, data }: { campId: string; data: { start_date: string; end_date: string; max_capacity: number | null } }): Promise<Camp> => {
      try {
        return await apiClient.post<Camp>(
          `/api/organizers/${organizerId}/camps/${campId}/dates`,
          data
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to create camp date:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizer-admin-camps', organizerId] });
    },
  });
};

// DELETE /organizers/{organizer_id}/camps/{camp_id}/dates/{date_id} - Delete camp date
export const useDeleteCampDate = (organizerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campId, dateId }: { campId: string; dateId: string }): Promise<void> => {
      try {
        await apiClient.delete(`/api/organizers/${organizerId}/camps/${campId}/dates/${dateId}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to delete camp date:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizer-admin-camps', organizerId] });
    },
  });
};

// POST /organizers/{organizer_id}/camps/{camp_id}/images/upload-url - Get presigned S3 upload URL
export interface CampImageUploadUrlResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
}

export const useGetCampImageUploadUrl = (organizerId: string) => {
  return useMutation({
    mutationFn: async ({ campId }: { campId: string }): Promise<CampImageUploadUrlResponse> => {
      try {
        return await apiClient.post<CampImageUploadUrlResponse>(
          `/api/organizers/${organizerId}/camps/${campId}/images/upload-url`,
          {}
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to get image upload URL:', errorMessage);
        throw new Error(errorMessage);
      }
    },
  });
};

// POST /organizers/{organizer_id}/camps/{camp_id}/images - Save uploaded image
export const useSaveCampImage = (organizerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campId, key, imageType }: { campId: string; key: string; imageType: string }): Promise<Camp> => {
      try {
        return await apiClient.post<Camp>(
          `/api/organizers/${organizerId}/camps/${campId}/images`,
          { temp_s3_key: key, image_type: imageType }
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to save camp image:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizer-admin-camps', organizerId] });
    },
  });
};

// DELETE /organizers/{organizer_id}/camps/{camp_id}/images/{image_type} - Delete camp image
export const useDeleteCampImage = (organizerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campId, imageType }: { campId: string; imageType: string }): Promise<void> => {
      try {
        await apiClient.delete(`/api/organizers/${organizerId}/camps/${campId}/images/${imageType}`);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to delete camp image:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizer-admin-camps', organizerId] });
    },
  });
};

// PATCH /organizers/{organizer_id}/camps/{camp_id}/status - Toggle camp enabled state
export const useToggleCampEnabled = (organizerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ campId, status }: { campId: string; status: string }): Promise<Camp> => {
      try {
        return await apiClient.patch<Camp>(
          `/api/organizers/${organizerId}/camps/${campId}/status`,
          { status: status }
        );
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to toggle camp enabled state:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizer-admin-camps', organizerId] });
    },
  });
};
